import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Header } from '@components/Header';
import DiagonalArrow from '@components/icons/DiagonalArrow.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { TagInfoRounded } from '@components/core-ui/Tag/variations/TagInfoRounded';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { parseEndOfDayFromQueryParam } from '@helpers/parseEndOfDayFromQueryParam';
import { parseStartOfDayFromQueryParam } from '@helpers/parseStartOfDayFromQueryParam';
import { serializeDateToQueryParam } from '@helpers/serializeDateToQueryParam';
import { createFetchBillingAccountQueryFn } from '@pages/Admin/BillingAccountDetailsPage/api/fetchBillingAccount';
import { createFetchBillingAccountTransactionsQueryFn } from '@pages/Admin/BillingAccountDetailsPage/api/fetchBillingAccountTransactions';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { TransactionType } from '@pages/User/BillingAccountDetailsPage/components/TransactionType';
import { routes } from '@routes';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { transactionsKeys, TTransactionsListParams } from 'app/queries/transactions/transactionsKeys';
import { TPaginatedResponse } from 'app/types/API/TPaginatedResponse';
import { TJobsFilter } from 'app/types/TJobsFilter';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { createFetchAdminTransactionsQueryFn } from '../../api/fetchAdminTransactions';
import { fetchAllTransactionsForExport } from '../../api/fetchAllTransactionsForExport';
import { downloadAdminTransactionsCsv } from '../../helpers/downloadAdminTransactionsCsv';
import { downloadBillingScopedTransactionsCsv } from '../../helpers/downloadBillingScopedTransactionsCsv';
import { filterTransactionRows } from '../../helpers/filterTransactionRows';
import { mapTransactionResponse } from '../../helpers/mapTransactionResponse';
import { normalizeTransactionsResponse } from '../../helpers/normalizeTransactionsResponse';
import { TransactionRow } from '../../types/TransactionRow';
import { Tabs } from '../Tabs';
import { Cell } from './styles/Cell';
import { CellContent } from './styles/CellContent';
import { CellRowContent } from './styles/CellRowContent';
import { MissingValue } from './styles/MissingValue';
import { OrgCell } from './styles/OrgCell';
import { ReceiptUrl } from './styles/ReceiptUrl';
import { StyledLink } from './styles/StyledLink';
import { Wrapper } from './styles/Wrapper';

const SEARCH_DEBOUNCE_MS = 300;

type TProps = {
  billingAccountId?: string;
};

export const Content = ({ billingAccountId }: TProps) => {
  const navigate = useNavigate();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal } = useModalContext();
  const { searchParams, page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize, updateParams } =
    useListQueryParams({
      defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
    });
  const filter = useMemo<TJobsFilter>(() => {
    const from = parseStartOfDayFromQueryParam(searchParams.get('from'));
    const to = parseEndOfDayFromQueryParam(searchParams.get('to'));
    return { from, to };
  }, [searchParams]);
  const isBillingAccountScoped = Boolean(billingAccountId);
  const baId = billingAccountId ?? '';
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;

  const baTransactionsQuery = useQuery({
    queryKey: billingKeys.transactionsByAccount(baId),
    queryFn: createFetchBillingAccountTransactionsQueryFn(call),
    enabled: isBillingAccountScoped,
  });
  const baAccountQuery = useQuery({
    queryKey: billingKeys.account(baId),
    queryFn: createFetchBillingAccountQueryFn(call),
    enabled: isBillingAccountScoped,
  });
  const billingAccountName = isBillingAccountScoped && !baAccountQuery.isError ? baAccountQuery.data?.name ?? '' : '';
  const billingAccountOrganizationName =
    isBillingAccountScoped && !baAccountQuery.isError ? baAccountQuery.data?.organization?.name ?? '' : '';

  const scopedRows = useMemo(() => {
    if (!isBillingAccountScoped || !baTransactionsQuery.data) {
      return [];
    }
    const normalized = normalizeTransactionsResponse(baTransactionsQuery.data)
      .map(mapTransactionResponse)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return filterTransactionRows(normalized, { search, filter });
  }, [baTransactionsQuery.data, filter, isBillingAccountScoped, search]);

  const listParams = useMemo<TTransactionsListParams>(() => {
    const fromParam = Math.floor(Number(filter?.from ?? null) / 1000) || '';
    const toParam = Math.floor(Number(filter?.to ?? null) / 1000) || '';
    return {
      page,
      pageSize,
      orderBy,
      orderType,
      search: debouncedSearch,
      from: fromParam.toString(),
      to: toParam.toString(),
    };
  }, [debouncedSearch, filter?.from, filter?.to, orderBy, orderType, page, pageSize]);

  const adminTransactionsQuery = useQuery({
    queryKey: transactionsKeys.list(listParams),
    queryFn: createFetchAdminTransactionsQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isBillingAccountScoped && !isSearchSettling,
  });

  const transactions: TPaginatedResponse<TransactionRow> = isBillingAccountScoped
    ? {
        data: scopedRows.slice(pageSize * (page - 1), pageSize * page),
        count: scopedRows.length,
      }
    : adminTransactionsQuery.data ?? { data: [], count: 0 };

  const exportMutation = useMutation({
    mutationFn: async () => {
      if (isBillingAccountScoped && billingAccountId) {
        const response = await queryClient.fetchQuery({
          queryKey: billingKeys.transactionsByAccount(baId),
          queryFn: createFetchBillingAccountTransactionsQueryFn(call),
          staleTime: 0,
        });
        const normalized = normalizeTransactionsResponse(response)
          .map(mapTransactionResponse)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        const filtered = filterTransactionRows(normalized, { search, filter });
        return downloadBillingScopedTransactionsCsv(filtered, { billingAccountId, billingAccountName });
      }

      const allTransactions = await fetchAllTransactionsForExport(call, {
        search,
        from: listParams.from,
        to: listParams.to,
      });
      return downloadAdminTransactionsCsv(allTransactions.data);
    },
  });

  const exportCSV = () => {
    if (!exportMutation.isPending) {
      exportMutation.mutate();
    }
  };

  const setFilter = (arg: TJobsFilter) => {
    updateParams(
      {
        from: serializeDateToQueryParam(arg.from) || null,
        to: serializeDateToQueryParam(arg.to) || null,
      },
      { resetPage: true },
    );
  };

  const isLoading = isBillingAccountScoped
    ? baTransactionsQuery.isFetching || exportMutation.isPending
    : adminTransactionsQuery.isFetching || isSearchSettling || exportMutation.isPending;

  const openFiltersModal = () => {
    openModal(ModalNamesEnum.AdminTransactionsFilters, {
      filter,
      filterOnChange: setFilter,
      onSubmit: setFilter,
    });
  };

  const leftSideContent = [<Tabs key="TransactionsTabs" currentTab="Transactions" />];
  if (isBillingAccountScoped) {
    if (billingAccountOrganizationName) {
      leftSideContent.push(
        <TagInfoRounded key="OrganizationScope">Organization: {billingAccountOrganizationName}</TagInfoRounded>,
      );
    }
  }

  return (
    <>
      <Helmet>
        <title>Transactions - AutoScript Admin</title>
      </Helmet>
      <Header
        title="Transactions"
        backRoute={isBillingAccountScoped ? routes.adminTransactions.make() : undefined}
        buttonLabel={transactions.data?.length ? 'Export CSV' : undefined}
        buttonOnClick={transactions.data?.length ? () => exportCSV() : undefined}
        secondButtonLabel={isBillingAccountScoped ? 'All Transactions' : undefined}
        secondButtonOnClick={isBillingAccountScoped ? () => navigate(routes.adminTransactions.make()) : undefined}
        setSearch={setSearch}
        search={search}
        onFilterClick={openFiltersModal}
        filterCounter={filter.from || filter.to ? 1 : 0}
        leftSideContent={leftSideContent}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>Created</Th>
                <Th>Title</Th>
                <Th>Organization</Th>
                <Th>Amount</Th>
                <Th>Code</Th>
                <Th>Description</Th>
                <Th>Type</Th>
                <Th style={{ width: '85px', maxWidth: '85px' }}>Receipt Url</Th>
              </Tr>
            </Thead>
            <Tbody>
              {transactions.data?.map((data, index) => {
                const descriptionParts = (data.description ?? '').split(' ');
                return (
                  <Tr key={data.id ?? index}>
                    <Td>
                      <CellContent>{index + 1}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                    </Td>
                    <Td>
                      <Cell>
                        <div className="my-1">
                          {data.title} <br />
                          <small className="text-858dbd text-xs">Charge ID: {data.stripeChargeId ?? data.id}</small>
                          {data.userResponsibleEmail ? (
                            <div>
                              <small className="text-858dbd text-xs">
                                Actioned by: {data.userResponsibleName} &lt;{data.userResponsibleEmail}&gt;
                              </small>
                            </div>
                          ) : null}
                          {data.jobId ? (
                            <div>
                              <small className="text-858dbd text-xs">
                                Related job:{' '}
                                <StyledLink to={routes.adminJobDetails.make(data.jobId)}>{data.jobId}</StyledLink>
                              </small>
                            </div>
                          ) : null}
                        </div>
                      </Cell>
                    </Td>
                    <Td>
                      <CellRowContent>
                        {data.organization ? (
                          <OrgCell>
                            <StyledLink to={routes.adminOrganizationDetails.make(data.organization?.id)}>
                              {data.organization?.name}
                            </StyledLink>
                          </OrgCell>
                        ) : (
                          <MissingValue>—</MissingValue>
                        )}
                      </CellRowContent>
                    </Td>
                    <Td>
                      <CellContent>{formatCentsPrice(data.amount ?? 0)}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>
                        <div>{data.code?.code ?? data.codeId}</div>
                      </CellContent>
                    </Td>
                    <Td>
                      <small>
                        <p className="block">{descriptionParts.slice(0, 3).join(' ')}</p>
                        <p className="block">{descriptionParts.slice(3, 6).join(' ')}</p>
                        <p className="block">{descriptionParts.slice(6).join(' ')}</p>
                      </small>
                    </Td>
                    <Td>
                      <CellContent>
                        <TransactionType type={data.code?.status ?? data.codeId} />
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>
                        {data.stripeReceiptUrl ? (
                          <ReceiptUrl href={data.stripeReceiptUrl} target="_blank" rel="noreferrer">
                            Receipt{' '}
                            <div className="mt-2 ml-1">
                              <DiagonalArrow />
                            </div>
                          </ReceiptUrl>
                        ) : null}
                      </CellContent>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
        <PagePagination
          totalItemsCount={transactions.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Wrapper>
    </>
  );
};
